import React, { useMemo, useState } from "react";
// import SubSystemSettingsModal from "../modals/SubSystemSettingsModal";
import type { SubsystemInstance } from "../types/subsystems";
import "../styles/SubSystemsSection.scss";
import { ToggleOffGrayIcon, ToggleOnGreenIcon } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { useThermalDesign, useSubsystemConfig } from "../../../ThermalDesignContext";
import { useSelectedFace } from "./visualization/useSelectedFace";
import { FACE_TO_DECK_MAP } from "../types/subsystems";
import { useSubsystemsToggleSync } from "../hooks/ThermalDesignerHooks";

const ToggleButton: React.FC<{ enabled: boolean; onClick: () => void }> = ({ enabled, onClick }) => (
  <button className={`thermal-toggle-btn${enabled ? " active" : ""}`} type="button" onClick={onClick} aria-pressed={enabled}>
    {enabled ? <ToggleOnGreenIcon /> : <ToggleOffGrayIcon />}
  </button>
);

interface SubsystemBoxProps {
  subsystem: SubsystemInstance;
  isSettingsOpen: boolean;
  onSettingsToggle: () => void;
}

const SubsystemBox: React.FC<SubsystemBoxProps> = ({ subsystem, isSettingsOpen, onSettingsToggle }) => {
  const { selectedSubsystemId, setSelectedSubsystemId } = useSubsystemConfig();

  const handleBoxClick = () => {
    // Toggle selection in 3D
    setSelectedSubsystemId(selectedSubsystemId === subsystem.id ? null : subsystem.id);
    // Toggle settings display
    onSettingsToggle();
  };

  return (
    <>
      <div className={`thermal-subsystem-box${selectedSubsystemId === subsystem.id ? " thermal-subsystem-box--active" : ""}`} onClick={handleBoxClick}>
        <div className="thermal-subsystem-box-left">
          <div className="thermal-subsystem-name">{subsystem.label}</div>
          <div className="thermal-subsystem-info-row">
            <div className="thermal-subsystem-property">
              <span className="thermal-subsystem-property-label">Max Power consumption:</span>
              <span className="thermal-subsystem-property-value">{subsystem.thermal.powerDissipation.toFixed(2)} W</span>
            </div>
            <div className="thermal-subsystem-property">
              <span className="thermal-subsystem-property-label">Thermal Design Range:</span>
              <span className="thermal-subsystem-property-value">{`[${subsystem.thermal.operatingTempRange.min}, ${subsystem.thermal.operatingTempRange.max}] °C`}</span>
            </div>
          </div>
        </div>
      </div>
      {/* {isSettingsOpen && <SubSystemSettingsModal subsystemInstance={subsystem} />} */}
    </>
  );
};

const SubSystemsSection: React.FC = () => {
  const { subsystemInstances } = useThermalDesign();
  const { showSubsystemsView, setShowSubsystemsView } = useSubsystemConfig();
  const selectedFace = useSelectedFace();
  const [openSettingsId, setOpenSettingsId] = useState<string | null>(null);

  const selectedDeck = useMemo(() => {
    if (!selectedFace) return null;
    return FACE_TO_DECK_MAP[selectedFace] || null;
  }, [selectedFace]);

  const visibleSubsystems = useMemo(() => {
    if (!selectedDeck) return [];
    return subsystemInstances.filter((instance) => instance.mountLocation.deck === selectedDeck && instance.enabled);
  }, [subsystemInstances, selectedDeck]);

  // Use the modularized hook for toggle management
  const { handleToggle } = useSubsystemsToggleSync(showSubsystemsView, setShowSubsystemsView);

  const handleSettingsToggle = (subsystemId: string) => {
    setOpenSettingsId(openSettingsId === subsystemId ? null : subsystemId);
  };

  if (!selectedFace || !selectedDeck || visibleSubsystems.length === 0) return null;

  return (
    <div className="thermal-subsystems-section thermal-subsystems-flex typography-montserrat">
      <div className="thermal-subsystems-header">
        <div className="thermal-coating-toggle-row" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span className="thermal-subsystems-toggle">Subsystems</span>
          <ToggleButton enabled={showSubsystemsView} onClick={handleToggle} />
        </div>
      </div>
      {showSubsystemsView && (
        <div className="thermal-subsystems-body thermal-subsystems-body-fullwidth">
          {visibleSubsystems.length > 0 ? (
            <div className="thermal-subsystems-list">
              {visibleSubsystems.map((subsystem) => (
                <SubsystemBox 
                  key={subsystem.id} 
                  subsystem={subsystem} 
                  isSettingsOpen={openSettingsId === subsystem.id}
                  onSettingsToggle={() => handleSettingsToggle(subsystem.id)}
                />
              ))}
            </div>
          ) : (
            <div className="thermal-subsystems-empty">No subsystems on this face</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubSystemsSection;
